import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Input from '@Components/Input';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import { ButtonWithIcon } from '@els/els-react--button';
import { errorMessage } from '@Pages/Login/BulkIdRegister/BulkIdRegisterConstants';
import LoginFormHeader from '../../Components/LoginFormHeader';

import './BulkIdRegisterForm.scss';
import '../../Components/LoginForm.scss';

function BulkIdRegisterForm({ ipAddress, loginFail, isLoading, submit, data, setData, clearLoginFail }) {
  const spinnerId = 'bulkIdRegisterForm';
  const [showPassword, setShowPassword] = useState(false);
  const [touchedPassword, setTouchedPassword] = useState(false);
  const [touchedId, setTouchedId] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const isBulkIdInvalidError = get(loginFail, 'bulkIdInvalid');
  const showPasswordValidationMessage = (touchedPassword || isFormSubmitted) && !isBulkIdInvalidError && !data.bulkPassword;
  const showIdValidationMessage = isBulkIdInvalidError || ((touchedId || isFormSubmitted) && !data.bulkId);

  // eslint-disable-next-line consistent-return
  const resolveIdValidationMessage = () => {
    if (touchedId && !isBulkIdInvalidError && !data.bulkId) {
      return 'Bulk ID is required.';
    }

    if (isBulkIdInvalidError && !data.bulkId) {
      return errorMessage;
    }
  };

  const submitHandler = () => {
    if (showPasswordValidationMessage || showIdValidationMessage) {
      return;
    }

    setTouchedId(false);
    setTouchedPassword(false);

    if (!isFormSubmitted) {
      setIsFormSubmitted(true);
    }
    submit();
  };

  const touchBulkId = () => {
    if (touchedId) {
      return;
    }
    setTouchedId(true);
  };

  const touchPassword = () => {
    if (touchedPassword) {
      return;
    }
    setTouchedPassword(true);
  };

  const removeLoginFailError = () => {
    if (!loginFail) {
      return;
    }
    clearLoginFail();
  };

  const changeBulkId = (value) => {
    setData({
      ...data,
      bulkId: value,
    });
    if (touchedId && loginFail) {
      removeLoginFailError();
    }
  };

  const changeBulkPassword = (value) => {
    setData({
      ...data,
      bulkPassword: value,
    });
  };

  const togglePassword = () => setShowPassword(!showPassword);

  return (
    <div className="login-form login-page-form-wrapper bulk-id-register-form">
      <LoginFormHeader
        ipAddress={ipAddress}
        actionMessage="Registration ID Redemption"
        infoMessage={
          <span>
            Registration IDs allow you to associate your Reaxys username with a new organization and thus access Reaxys features and content
            to which that organization has entitlements. Please enter your Registration ID and password below, then click the{' '}
            <strong>Continue</strong> button.
          </span>
        }
      />
      <form name="bulkIdRegisterForm">
        <div className="login-form__content">
          {isLoading && <SimpleSpinner id={spinnerId} />}
          <div className="els-text-input">
            <Input
              clearButton={false}
              id="bulk-id-register-id"
              name="bulkId"
              classNameInput="e2e-bulk-id-register-id"
              animatedPlaceholder
              placeholder="Registration ID"
              value={data.bulkId}
              onChange={changeBulkId}
              onMouseDown={touchBulkId}
              validationMessage={showIdValidationMessage && resolveIdValidationMessage()}
              required
              debounceDelay={0}
            />
          </div>
          <div className="els-text-input">
            <Input
              id="bulk-id-register-password"
              name="bulkPassword"
              clearButton={false}
              classNameInput="e2e-bulk-id-register-password"
              animatedPlaceholder
              placeholder="Password"
              value={data.bulkPassword}
              onMouseDown={touchPassword}
              onChange={changeBulkPassword}
              validationMessage={showPasswordValidationMessage && 'Password is required'}
              required
              debounceDelay={0}
              type={showPassword ? 'text' : 'password'}
              afterLabelContent={
                // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                <span className="rx-btn-icon" onMouseDown={togglePassword} onMouseUp={togglePassword}>
                  <i className="ss-view" />
                </span>
              }
            />
          </div>
          <div className="login-form__content_actions">
            <div className="login-form__content_actions-button">
              <ButtonWithIcon
                type="primary"
                sprite="ChevronRight"
                iconRight
                disabled={isLoading}
                onClick={submitHandler}
                className="e2e-login-sign-in">
                Continue
              </ButtonWithIcon>
            </div>
          </div>
        </div>
      </form>

      <div className="bulk-id-register-form__messages">
        <span className="login-form__message">
          <a
            className="u-els-color-link-inline"
            tabIndex="0"
            target="_blank"
            href="http://www.elsevier.com/privacypolicy?dgcid=RN_AGCM_Sourced_300005028"
            rel="noreferrer">
            Privacy Policy
          </a>
        </span>
        <span className="login-form__message">
          <a
            className="u-els-color-link-inline"
            tabIndex="0"
            target="_blank"
            href="http://www.elsevier.com/termsconditions?dgcid=RN_AGCM_Sourced_300005028"
            rel="noreferrer">
            Terms and Conditions
          </a>
        </span>
      </div>
    </div>
  );
}

BulkIdRegisterForm.propTypes = {
  data: PropTypes.shape({ bulkId: PropTypes.string, bulkPassword: PropTypes.string }),
  setData: PropTypes.func,
  ipAddress: PropTypes.string,
  loginFail: PropTypes.object,
  isLoading: PropTypes.bool,
  submit: PropTypes.func.isRequired,
  clearLoginFail: PropTypes.func.isRequired,
};

export default BulkIdRegisterForm;
